import { Loader2 } from 'lucide-react';
import { Panel, PhoneValue } from './sales-ui';
import { useProspectLeadsQuery, type ProspectLead } from '@/client/api/prospect/sources.query';
import { SHARE_SITE_COLOR, type ShareSiteId } from '../prospect-share';

// Replaces the old "search never names the site" blurb with the actual leads:
// named, phone-reachable sellers who already list the most elsewhere, deduped to
// one row per contactable person (ff-prospect /analytics/top-prospects). Names
// the source each was found on and shows the number to call.

const SOURCE_LABEL: Record<string, string> = {
  jiji: 'Jiji',
  meqasa: 'meQasa',
  gpc: 'GPC',
  lamudi: 'Lamudi',
  seventerra: '7th Terra',
  ghanaweb: 'GhanaWeb',
  realtor: 'Realtor',
  privateproperty: 'Private Property',
  autochek: 'Autochek',
  oxglow: 'Oxglow',
  platform: 'FeelyFeely',
};

function SourceTag({ source }: { source: string }) {
  const color = source in SHARE_SITE_COLOR ? SHARE_SITE_COLOR[source as ShareSiteId] : '#64748b';
  return (
    <span
      className="inline-flex items-center rounded-sm px-1.5 py-0.5 text-[11px] font-medium text-white"
      style={{ backgroundColor: color }}
    >
      {SOURCE_LABEL[source] ?? source}
    </span>
  );
}


function LeadRow({ lead }: { lead: ProspectLead }) {
  return (
    <tr className="border-t border-gray-100">
      <td className="py-2 pr-4">
        <div className="text-gray-900">{lead.name || 'Unnamed seller'}</div>
        {lead.kind ? <div className="text-[11px] text-gray-500">{lead.kind}</div> : null}
      </td>
      <td className="py-2 pr-4"><SourceTag source={lead.source} /></td>
      <td className="py-2 pr-4"><PhoneValue phone={lead.phone} /></td>
      <td className="py-2 pr-4 text-right tabular-nums text-gray-900">{lead.listings.toLocaleString()}</td>
      <td className="py-2 pr-4 text-right tabular-nums text-gray-500">{lead.verticals}</td>
      <td className="py-2 pr-4 text-right text-gray-500">{lead.last_seen ?? '—'}</td>
    </tr>
  );
}

export function LeadsPanel({
  country = 'ghana',
  isPlaces = false,
  onOpenSearch,
}: {
  country?: string;
  isPlaces?: boolean;
  onOpenSearch: () => void;
}) {
  // Places has no indexed marketplace equivalent — no leads to show there.
  const q = useProspectLeadsQuery(country, undefined, undefined, !isPlaces);
  const leads = q.data?.prospects ?? [];

  return (
    <Panel
      title="Find people to bring over"
      actions={
        <button
          type="button"
          className="text-sm font-medium text-gray-800 hover:underline"
          onClick={onOpenSearch}
        >
          Open Search
        </button>
      }
    >
      <p className="text-sm text-gray-600">
        Sellers already listing the most on other sites, named with a number to call. One row is one person
        (deduped across sites). Adding them to the pool does not claim them.
      </p>
      {isPlaces ? (
        <p className="mt-3 text-sm text-gray-500">
          Places has no indexed marketplace equivalent — Search will say so rather than return everyone.
        </p>
      ) : q.isLoading ? (
        <div className="mt-3 flex items-center gap-2 py-4 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading leads…
        </div>
      ) : q.isError ? (
        <p className="mt-3 text-sm text-gray-500">Leads are unavailable right now.</p>
      ) : leads.length === 0 ? (
        <p className="mt-3 text-sm text-gray-500">No phone-reachable sellers indexed yet.</p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500">
                <th className="py-2 pr-4 font-medium">Seller</th>
                <th className="py-2 pr-4 font-medium">Found on</th>
                <th className="py-2 pr-4 font-medium">Phone</th>
                <th className="py-2 pr-4 text-right font-medium">Listings</th>
                <th className="py-2 pr-4 text-right font-medium">Verticals</th>
                <th className="py-2 pr-4 text-right font-medium">Last seen</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead, i) => (
                <LeadRow key={`${lead.phone ?? lead.name ?? 'x'}-${lead.source}-${i}`} lead={lead} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
